import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Send, FlaskConical, Loader2 } from "lucide-react";
import { analyticsApi } from "@/services/apiClient";
import { useFilterStore, isReadOnlyEnv } from "@/stores/filterStore";

const CARD = "rounded-2xl bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)]";
const INPUT = "w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none";

export default function MailComposePage() {
    const [searchParams] = useSearchParams();
    const readOnly = isReadOnlyEnv(useFilterStore((s) => s.env));

    const [emailsRaw, setEmailsRaw] = useState(
        () => (searchParams.get("emails") || "").split(",").join("\n")
    );
    const [subject, setSubject] = useState("");
    const [bodyText, setBodyText] = useState("");
    const [bodyHtml, setBodyHtml] = useState("");
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);

    // mail_ops.py 80행의 JS 버전 — 분리 → "@" 필터 → 소문자 → Set으로 중복 제거
    const parsedEmails = [
        ...new Set(
            emailsRaw
                .split(/[\s,;]+/)
                .map((e) => e.trim())
                .filter((e) => e.includes("@"))
                .map((e) => e.toLowerCase())
        ),
    ];

    const canSendTest = !!subject.trim() && !!(bodyText.trim() || bodyHtml.trim()) && !sending && !readOnly;
    const canSendReal = canSendTest && parsedEmails.length > 0 && parsedEmails.length <= 500;

    async function send(testMode) {
        if (readOnly) { setResult({ error: "STG 조회 상태에서는 메일 발송이 비활성화됩니다." }); return; }
        if (!testMode && !window.confirm(`${parsedEmails.length}명에게 실제 발송합니다. 계속?`)) return;
        setSending(testMode ? "test" : "real");
        setResult(null);
        try {
            const res = await analyticsApi.adminMailSend({
                emails: parsedEmails, subject, bodyText, bodyHtml, testMode,
            });
            setResult(res);
        } catch (e) {
            setResult({ error: e.message });
        } finally {
            setSending(false);
        }
    }

    return (
        <div className="min-h-screen bg-ink-50">
            <div className="mx-auto max-w-2xl space-y-5 p-6 pb-16">
                <header>
                    <h1 className="flex items-center gap-2 text-xl font-bold text-ink-900">
                        <Mail className="h-5 w-5 text-brand-600" />
                        일괄 안내 메일
                    </h1>
                    <p className="mt-1.5 text-sm text-ink-500">
                        일괄 전송이지만, 수신자에게는 본인 앞으로만 온 메일이 보인다.
                    </p>
                </header>

                <section className={CARD}>
                    <label className="mb-1.5 block text-[13px] font-semibold text-ink-800">
                        수신자
                    </label>
                    <textarea rows={5} value={emailsRaw} onChange={(e)=> setEmailsRaw(e.target.value)} placeholder="쉼표 또는 줄바꿈으로 구분" className={INPUT} />
                    <div className="mt-1.5 text-xs text-ink-400">
                        유효 주소 <b className="text-ink-700">{parsedEmails.length}명</b> (중복 제거됨)
                        {parsedEmails.length > 500 && (
                            <span className="ml-2 text-red-600"> 최대 500명까지 발송 가능 </span>
                        )}
                    </div>
                </section>

                <section className={`${CARD} space-y-3`}>
                    <div>
                        <label className="mb-1.5 block text-[13px] font-semibold text-ink-800"> 제목 </label>
                        <input value={subject} onChange={(e) => setSubject(e.target.value)} className={INPUT} />
                    </div>
                    <div>
                        <label className="mb-1.5 block text-[13px] font-semibold text-ink-800">본문(텍스트)</label>
                        <textarea rows={8} value={bodyText} onChange={(e) => setBodyText(e.target.value)} className={INPUT} />
                    </div>
                    <div>
                        <label className="mb-1.5 block text-[13px] font-semibold text-ink-800">
                            본문 (HTML) <span className="font-normal text-ink-400"> - 선택 </span>
                        </label>
                        <textarea rows={6} value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} className={INPUT} />
                    </div>
                </section>

                <section className={`${CARD} space-y-3`}>
                    <div className="flex flex-wrap gap-2">
                        <button onClick={() => send(true)}
                        disabled={!canSendTest}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-40">
                            {sending === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}
                            테스트 발송
                        </button>
                        <button
                        onClick={() => send(false)}
                        disabled={!canSendReal}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-40">
                            {sending === "real" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            {parsedEmails.length} 명에게 발송
                        </button>
                    </div>
                    <p className="text-xs text-ink-400">
                        테스트 발송은 수신자와 무관하게 ai@sedaily.com 1통 - 제목에 [테스트]가 자동으로 붙는다.
                        실발송 전 렌더링을 먼저 확인한다.
                    </p>
                </section>

                {result && (
            <section className={CARD}>
              {result.error ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5
  text-sm text-red-700">
                  발송 실패 — {result.error}
                </div>
              ) : (
                <>
                  <div className="text-sm text-ink-700">
                    {result.testMode && (
                      <span className="mr-2 rounded-full bg-brand-50 px-2 py-0.5
                        text-[11px] font-medium text-brand-700">테스트</span>
                                            )}
                                            요청 <b>{result.requested}</b> · 성공{" "}
                                            <b className="text-emerald-600">{result.sentCount}</b> · 실패{" "}
                                            <b className={result.failedCount ? "text-red-600" : 
                        ""}>{result.failedCount}</b>
                                            <span className="ml-2 text-xs text-ink-400">by {result.sentBy}</span>
                                        </div>
                                        {result.failed?.length > 0 && (
                                            <ul className="mt-2 space-y-1 rounded-lg border border-amber-200/70
                        bg-amber-50/50 px-3 py-2 text-[13px]">
                                            {result.failed.map((f) => (
                                                <li key={f.email}>
                                                <span className="font-medium text-ink-800">{f.email}</span>
                                                <span className="mx-1.5 text-ink-400">—</span>
                                                <span className="text-ink-600">{f.error}</span>
                                                </li>
                                            ))}
                                            </ul>
                                        )}
                                        </>
                                    )}
                                    </section>
                                )}
                                </div>
                            </div>
                            );
                        }
